/**
 * DubbingContext
 *
 * Provides dubbing state and actions to the component tree, eliminating
 * prop drilling through MediaUploadSection, LanguageConfigSection,
 * AdvancedSettingsSection, and DubbingResultsSection.
 */

import React, { useMemo, useCallback, useState, useEffect } from 'react';
import { useDubbingState, useDubbingApi } from './hooks';
import { useLLMModels } from '@/hooks/useLLMModels';
import { useMediaDuration } from '@/hooks/useMediaDuration';
import { useToast } from '@/hooks/useToast';
import { useTranslation } from '@/hooks/useTranslation';
import { useRequireAuth } from '@/hooks/useRequireAuth';
import type { MediaType } from '@/components/common/MediaUploader';
import type { AudioLanguageConfig } from '../common/LanguageSelector';
import type { AudioTrackMode, LLMProvider } from '@/types';
import { DubbingContext, type DubbingContextValue } from './DubbingContext.context';
import { toApiVoiceConfigs } from './voiceConfigs';
import { COST_GATE_OPEN, type CostGateStatus } from '../../utils/costGate';

interface DubbingProviderProps {
  children: React.ReactNode;
}

export function DubbingProvider({ children }: DubbingProviderProps): JSX.Element {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const { requireAuth } = useRequireAuth();

  const { state, actions } = useDubbingState();

  const { availableModels: llmModels, selectedModel: llmSelectedModel } = useLLMModels(
    state.llmProvider,
    (model) => actions.setLlmModel(model),
    { enabled: state.enableLlmRefinement }
  );

  const [costGate, setCostGate] = useState<CostGateStatus>(COST_GATE_OPEN);

  const { startDubbing, handleDownload, handleOpenFolder, cancelDubbing } = useDubbingApi({
    state,
    actions,
    showToast,
    t,
  });

  // Duration detection
  const { duration: mediaDurationFromHook } = useMediaDuration(
    state.mediaFile ?? null,
    state.mediaType === 'audio' ? 'audio' : 'video'
  );

  useEffect(() => {
    if (mediaDurationFromHook !== null) {
      actions.setMediaDuration(mediaDurationFromHook);
    }
  }, [mediaDurationFromHook, actions]);

  // Derived status flags
  const isUploading = state.status === 'uploading';
  const isDubbing = state.status === 'dubbing';
  const hasResult = Boolean(state.dubbingResult?.success || state.audioDubbingResult?.success);
  const showForm = !isDubbing && !hasResult;

  // Which operation this card is really asking about. Audio and video dubbing
  // are two routes with two request models and two prices; asking /api/v1/dubbing
  // about an audio file and passing type:'audio' alongside it made the card
  // describe a job neither route would run.
  const estimateOperationPath =
    state.mediaType === 'audio' ? '/api/v1/dubbing/audio-dubbing' : '/api/v1/dubbing';

  // Token estimate params: the dubbing request's OWN fields, so the quote is
  // computed from exactly what the submit below will send.
  //
  // target_languages is PLURAL and carries every language, because that is what
  // the routes charge for -- they loop over it. This card used to send one
  // language and a separate target_languages_count; the count was then deleted
  // to stop it tripping the new "refuse an unpriced parameter" rule, which hid a
  // 5x under-quote (698 tokens quoted for a five-language dub that charges
  // 3,490) instead of fixing it. The price reads the real field now.
  const estimateParams = useMemo(() => {
    if (!state.mediaDuration || state.voiceConfigs.length === 0) {
      return null;
    }
    return {
      duration_seconds: state.mediaDuration,
      source_language: state.sourceLanguage || 'en',
      target_languages: state.voiceConfigs.map((config) => config.language),
      // The routes resolve the TTS provider from voice_configs[0], so the quote
      // is built by the SAME function the submit builds its payload with.
      voice_configs: toApiVoiceConfigs(state.voiceConfigs),
    };
  }, [state.mediaDuration, state.sourceLanguage, state.voiceConfigs]);

  // Handlers
  const handleMediaSelect = useCallback(
    (file: File, type: MediaType) => {
      actions.setMedia(file, type);
    },
    [actions]
  );

  const handleMediaRemove = useCallback(() => {
    actions.clearMedia();
  }, [actions]);

  const handleSourceLanguageChange = useCallback(
    (language: string) => {
      actions.setSourceLanguage(language);
    },
    [actions]
  );

  const handleVoiceConfigsChange = useCallback(
    (configs: AudioLanguageConfig[]) => {
      actions.setVoiceConfigs(configs);
    },
    [actions]
  );

  const handleAudioTrackModeChange = useCallback(
    (mode: AudioTrackMode) => {
      actions.setAudioTrackMode(mode);
    },
    [actions]
  );

  const handleEnableLlmRefinementChange = useCallback(
    (enabled: boolean) => {
      actions.setLlmRefinement(enabled);
    },
    [actions]
  );

  const handleLlmProviderChange = useCallback(
    (provider: LLMProvider) => {
      actions.setLlmProvider(provider);
    },
    [actions]
  );

  const handleLlmModelChange = useCallback(
    (model: string) => {
      actions.setLlmModel(model);
    },
    [actions]
  );

  const handleStartDubbing = useCallback(async () => {
    if (!state.mediaFile) {
      showToast(
        'error',
        t('dubbing.errors.noMediaTitle', { defaultValue: 'No File Selected' }),
        t('dubbing.errors.noMediaMessage', {
          defaultValue: 'Please select a video or audio file to dub.',
        })
      );
      return;
    }

    if (state.voiceConfigs.length === 0) {
      showToast(
        'error',
        t('dubbing.errors.noLanguagesTitle'),
        t('dubbing.errors.noLanguagesMessage')
      );
      return;
    }

    // Video dubbing requires an explicit source language: the backend video
    // pipeline has NO auto-detect (only AUDIO dubbing supports it).
    if (state.mediaType !== 'audio' && (!state.sourceLanguage || state.sourceLanguage === 'auto')) {
      showToast(
        'error',
        t('dubbing.errors.noSourceLanguageTitle', {
          defaultValue: 'Source Language Required',
        }),
        t('dubbing.errors.noSourceLanguageMessage', {
          defaultValue:
            'Video dubbing requires an explicit source language. Auto-detect is only available for audio files.',
        })
      );
      return;
    }

    requireAuth(async () => {
      await startDubbing(
        state.mediaFile!,
        state.voiceConfigs,
        state.sourceLanguage,
        state.audioTrackMode,
        state.enableLlmRefinement,
        state.llmProvider,
        llmSelectedModel
      );
    }, t('auth.loginRequired.dubbing'));
  }, [
    state.mediaFile,
    state.mediaType,
    state.voiceConfigs,
    state.sourceLanguage,
    state.audioTrackMode,
    state.enableLlmRefinement,
    state.llmProvider,
    llmSelectedModel,
    showToast,
    t,
    requireAuth,
    startDubbing,
  ]);

  const handleCancel = useCallback(() => {
    cancelDubbing();
  }, [cancelDubbing]);

  const value = useMemo<DubbingContextValue>(
    () => ({
      state,
      actions,
      isUploading,
      isDubbing,
      hasResult,
      showForm,
      llmModels,
      llmSelectedModel,
      costGate,
      setCostGate,
      estimateOperationPath,
      estimateParams,
      handleMediaSelect,
      handleMediaRemove,
      handleSourceLanguageChange,
      handleVoiceConfigsChange,
      handleAudioTrackModeChange,
      handleEnableLlmRefinementChange,
      handleLlmProviderChange,
      handleLlmModelChange,
      handleStartDubbing,
      handleCancel,
      handleDownload,
      handleOpenFolder,
    }),
    [
      state,
      actions,
      isUploading,
      isDubbing,
      hasResult,
      showForm,
      llmModels,
      llmSelectedModel,
      costGate,
      estimateOperationPath,
      estimateParams,
      handleMediaSelect,
      handleMediaRemove,
      handleSourceLanguageChange,
      handleVoiceConfigsChange,
      handleAudioTrackModeChange,
      handleEnableLlmRefinementChange,
      handleLlmProviderChange,
      handleLlmModelChange,
      handleStartDubbing,
      handleCancel,
      handleDownload,
      handleOpenFolder,
    ]
  );

  return <DubbingContext.Provider value={value}>{children}</DubbingContext.Provider>;
}
